import TwoFactorVerify from './TwoFactorVerify';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Building2, Home, LogIn, UserPlus, Key } from 'lucide-react';

const TenantLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState("");
  const [twoFAUserId, setTwoFAUserId] = useState("");
  const [registerForm, setRegisterForm] = useState({
    invitation_code: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: loginForm.email, password: loginForm.password });
      if (data.requires_2fa) {
        setShow2FA(true);
        setTwoFAEmail(data.email);
        setTwoFAUserId(data.user_id);
        setLoading(false);
        return;
      }
      if (data.access_token && data.role) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user_role", data.role);
        if (data.org_type) localStorage.setItem("org_type", data.org_type);
        if (data.user_id) localStorage.setItem("user_id", data.user_id);
        toast.success("Login successful!");
        setTimeout(() => { window.location.replace("/dashboard"); }, 100);
      } else {
        toast.error("Invalid response");
        setLoading(false);
      }
    } catch (err) {
      toast.error("Login failed: " + (err.response?.data?.detail || "Invalid credentials"));
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        invitation_code: registerForm.invitation_code,
        first_name: registerForm.first_name,
        last_name: registerForm.last_name,
        email: registerForm.email,
        password: registerForm.password,
        phone: registerForm.phone
      };
      const { data } = await api.post("/auth/tenant/register", payload);
      toast.success("Registration successful! Please login.");
      setActiveTab('login');
      setLoginForm({ email: registerForm.email, password: '' });
      setLoading(false);
    } catch (err) {
      console.error("Registration error:", err);
      const errorMsg = err.response?.data?.detail || "Invalid invitation code";
      toast.error("Registration failed: " + errorMsg);
      setLoading(false);
    }
  };

  if (show2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center py-12 px-4">
        <TwoFactorVerify
          email={twoFAEmail}
          userId={twoFAUserId}
          onSuccess={(data) => {
            toast.success("Login successful!");
            setTimeout(() => { window.location.replace("/dashboard"); }, 100);
          }}
          onBack={() => { setShow2FA(false); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Building2 className="h-8 w-8 text-emerald-600" />
            <span className="text-2xl font-bold">Estate Flow</span>
          </div>
          <div className="flex items-center justify-center space-x-2 text-purple-600">
            <Home className="h-6 w-6" />
            <span className="font-medium text-lg">Tenant Portal</span>
          </div>
          <CardTitle>Welcome</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" placeholder="tenant@example.com" value={loginForm.email} onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} required />
                </div>
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={loading}>
                  {loading ? 'Signing in...' : <><LogIn className="mr-2 h-4 w-4" />Sign In</>}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    Invitation Code
                  </Label>
                  <Input 
                    type="text" 
                    placeholder="Enter code from your landlord" 
                    value={registerForm.invitation_code} 
                    onChange={(e) => setRegisterForm({...registerForm, invitation_code: e.target.value.toUpperCase()})} 
                    required 
                    className="uppercase"
                  />
                  <p className="text-xs text-gray-500">Ask your landlord or property manager for this code</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input type="text" value={registerForm.first_name} onChange={(e) => setRegisterForm({...registerForm, first_name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input type="text" value={registerForm.last_name} onChange={(e) => setRegisterForm({...registerForm, last_name: e.target.value})} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={registerForm.email} onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input type="tel" placeholder="+1 (555) 000-0000" value={registerForm.phone} onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={registerForm.password} onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})} required />
                </div>
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={loading}>
                  {loading ? 'Registering...' : <><UserPlus className="mr-2 h-4 w-4" />Register</>}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <div className="mt-4 text-center">
            <Button variant="link" onClick={() => navigate('/')}>Back to Home</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantLogin;